/**
 * Shared API client for all IPA portals.
 *
 * Provides a factory function to create configured API clients with
 * optional authentication and credential support. Public portals use
 * the default (unauthenticated) export; authenticated portals use
 * createApiClient() with includeAuth/includeCredentials options.
 *
 * Security: Content-Type header is NOT set on GET/HEAD/OPTIONS requests
 * to avoid triggering unnecessary CORS preflight requests.
 *
 * Session expiry: when an authenticated client (`includeAuth: true`) receives a
 * 401, the session has expired or been revoked mid-session. The client invokes
 * a one-shot {@link handleUnauthorized} that, by default, clears local session
 * state and redirects to the login page (preserving `returnUrl`). This closes
 * the gap where a mid-session token expiry would otherwise leave the user
 * stranded on a half-broken page. Unauthenticated clients (public portals, the
 * login flow) are never redirected, since they legitimately receive 401s.
 *
 * Invariant this relies on: IPA backend services return **401 only for
 * session-expiry / authentication-required conditions** (e.g. the
 * `jwt-validation` guard, `SESSION_EXPIRED`/`UNAUTHORIZED` error codes).
 * Business-rule failures use 400 (e.g. `change-password` with a wrong current
 * password) and authorization failures use 403 — neither triggers a logout.
 * If a new authenticated endpoint ever returns 401 for a non-expiry reason,
 * that endpoint must be corrected to 400/403 rather than weakening this handler.
 *
 * @module shared/api/api-client
 */

import { performLogout } from '../auth/logout';

/**
 * Path prefix for admin API endpoints. Requests whose path begins with this
 * prefix are routed to the dedicated admin FQDN when one is configured.
 */
const ADMIN_PATH_PREFIX = '/api/v1/admin/';

/**
 * Reads a runtime-injected base URL from a `window` global.
 *
 * Base URLs are provided per environment by the deploy-time `config.js`
 * (see ipa-frontend/pipelines/templates/deploy-portal.yml), loaded before the
 * application module script. Returns an empty string when not running in a
 * browser or when the global is unset.
 */
function readWindowBaseUrl(key: string): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return ((window as unknown as Record<string, unknown>)[key] as string | undefined) ?? '';
}

/**
 * Resolves the base URL for a given request path.
 *
 * Admin-prefixed paths (`/api/v1/admin/*`) route to the dedicated admin FQDN
 * (`window.__IPA_ADMIN_API_BASE_URL__`) as part of the admin-endpoint
 * defense-in-depth migration (ADR-002 Phase 2b, Story #2726). All other paths
 * — and admin paths in environments where the admin base URL is not configured
 * — fall back to the public API base URL (`window.__IPA_API_BASE_URL__`), so
 * behavior is unchanged anywhere the cutover has not happened.
 *
 * @param path - The request path (e.g., `/api/v1/admin/dashboard`).
 * @returns The base URL to prefix the request path with.
 */
export function resolveApiBaseUrl(path: string): string {
  const publicBaseUrl = readWindowBaseUrl('__IPA_API_BASE_URL__');
  if (path.startsWith(ADMIN_PATH_PREFIX)) {
    const adminBaseUrl = readWindowBaseUrl('__IPA_ADMIN_API_BASE_URL__');
    return adminBaseUrl || publicBaseUrl;
  }
  return publicBaseUrl;
}

/** Generates a UUID v4 for correlation IDs. */
function uuid(): string {
  return crypto.randomUUID();
}

/** Maximum length for error messages displayed to the user. */
const MAX_ERROR_MESSAGE_LENGTH = 500;

/** Sanitizes an API error message by truncating and stripping control characters. */
function sanitizeErrorMessage(message: string): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = message.replace(/[\x00-\x1F\x7F]/g, '');
  return cleaned.length > MAX_ERROR_MESSAGE_LENGTH
    ? `${cleaned.substring(0, MAX_ERROR_MESSAGE_LENGTH)}...`
    : cleaned;
}

/** API response wrapper. */
export interface ApiResponse<T> {
  data: T;
  status: number;
}

/** API error with status code and optional response body. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Handler invoked when an *authenticated* request receives a 401, indicating
 * the session/token has expired or been revoked mid-session. See
 * {@link setUnauthorizedHandler} and {@link defaultUnauthorizedHandler}.
 */
export type UnauthorizedHandler = () => void;

/**
 * Builds the login redirect path, preserving the current location as a
 * `returnUrl` so the user lands back where they were after re-authenticating.
 * Mirrors the redirect pattern used by each portal's `ProtectedRoute`.
 */
function buildLoginRedirectPath(): string {
  if (typeof window === 'undefined') {
    return '/login/';
  }
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
  return `/login/?returnUrl=${returnUrl}`;
}

/**
 * Default reaction to a mid-session 401: clear local session state and redirect
 * to the login page (preserving `returnUrl`) via the shared {@link performLogout}
 * routine. `performLogout` always clears state and redirects even if the
 * server-side logout call fails, so the user is never trapped on a broken page.
 */
function defaultUnauthorizedHandler(): void {
  void performLogout({ redirectPath: buildLoginRedirectPath() });
}

let unauthorizedHandler: UnauthorizedHandler = defaultUnauthorizedHandler;

/**
 * One-shot guard so that a burst of concurrent 401s (e.g. a dashboard firing
 * several queries in parallel) triggers exactly one logout/redirect rather than
 * one per failed request. The guard is process-lifetime: once the redirect is
 * underway the page navigates away, and a full reload (the redirect uses
 * `window.location`) re-initializes this module with the guard cleared.
 */
let unauthorizedHandled = false;

/**
 * Overrides the handler invoked on a mid-session 401. Intended for tests (to
 * inject a spy) and for any portal needing bespoke expiry handling. Passing no
 * argument restores the default redirect-to-login behavior.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler = defaultUnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

/**
 * Restores the default unauthorized handler and clears the one-shot guard.
 * Exposed primarily so tests can reset module state between cases.
 */
export function resetUnauthorizedHandler(): void {
  unauthorizedHandler = defaultUnauthorizedHandler;
  unauthorizedHandled = false;
}

/** Invokes the unauthorized handler at most once per page lifetime. */
function handleUnauthorized(): void {
  if (unauthorizedHandled) {
    return;
  }
  unauthorizedHandled = true;
  unauthorizedHandler();
}

/** Configuration options for creating an API client instance. */
export interface ApiClientOptions {
  /** When true, reads Bearer token from sessionStorage and sets Authorization header. */
  includeAuth?: boolean;
  /** When true, sets credentials: 'include' on all fetch requests. */
  includeCredentials?: boolean;
}

/** Methods that carry a request body and need a Content-Type header. */
const BODY_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Builds common headers for an API request. */
function buildHeaders(
  options: { includeAuth: boolean; extra?: HeadersInit; setContentType: boolean },
): Headers {
  const headers = new Headers(options.extra);
  headers.set('X-Correlation-ID', uuid());

  if (options.setContentType && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (options.includeAuth) {
    const token = sessionStorage.getItem('ipa_auth_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  return headers;
}

/** Parses an error body from a failed response. */
async function parseErrorBody(response: Response): Promise<{ message: string; body: unknown }> {
  const body = await response.json().catch(() => null) as Record<string, unknown> | null;
  const message = typeof body?.message === 'string'
    ? sanitizeErrorMessage(body.message)
    : `Request failed with status ${response.status}`;
  return { message, body };
}

/** API client instance returned by createApiClient. */
export interface ApiClient {
  apiRequest: <T>(path: string, options?: RequestInit) => Promise<ApiResponse<T>>;
  apiUpload: <T>(path: string, formData: FormData, method?: string) => Promise<ApiResponse<T>>;
  apiBlobRequest: (path: string, options?: RequestInit) => Promise<Blob>;
}

/**
 * Creates a configured API client instance.
 *
 * @param clientOptions - Configuration for auth and credentials.
 * @returns Object with apiRequest, apiUpload, and apiBlobRequest methods.
 */
export function createApiClient(clientOptions: ApiClientOptions = {}): ApiClient {
  const { includeAuth = false, includeCredentials = false } = clientOptions;
  const credentialsOption = includeCredentials ? 'include' as const : undefined;

  async function apiRequestFn<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const method = (options.method ?? 'GET').toUpperCase();
    const headers = buildHeaders({
      includeAuth,
      extra: options.headers,
      setContentType: BODY_METHODS.has(method),
    });

    const response = await fetch(`${resolveApiBaseUrl(path)}${path}`, {
      ...options,
      headers,
      ...(credentialsOption ? { credentials: credentialsOption } : {}),
    });

    if (!response.ok) {
      if (includeAuth && response.status === 401) {
        handleUnauthorized();
      }
      const { message, body } = await parseErrorBody(response);
      throw new ApiError(message, response.status, body);
    }

    const data = await response.json() as T;
    return { data, status: response.status };
  }

  async function apiUploadFn<T>(
    path: string,
    formData: FormData,
    method: string = 'POST',
  ): Promise<ApiResponse<T>> {
    const headers = buildHeaders({
      includeAuth,
      setContentType: false,
    });

    const response = await fetch(`${resolveApiBaseUrl(path)}${path}`, {
      method,
      headers,
      body: formData,
      ...(credentialsOption ? { credentials: credentialsOption } : {}),
    });

    if (!response.ok) {
      if (includeAuth && response.status === 401) {
        handleUnauthorized();
      }
      const { message, body } = await parseErrorBody(response);
      throw new ApiError(message, response.status, body);
    }

    const data = await response.json() as T;
    return { data, status: response.status };
  }

  async function apiBlobRequestFn(
    path: string,
    options: RequestInit = {},
  ): Promise<Blob> {
    const headers = buildHeaders({
      includeAuth,
      extra: options.headers,
      setContentType: false,
    });

    const response = await fetch(`${resolveApiBaseUrl(path)}${path}`, {
      ...options,
      headers,
      ...(credentialsOption ? { credentials: credentialsOption } : {}),
    });

    if (!response.ok) {
      if (includeAuth && response.status === 401) {
        handleUnauthorized();
      }
      throw new ApiError(
        `Request failed with status ${response.status}`,
        response.status,
      );
    }

    return response.blob();
  }

  return {
    apiRequest: apiRequestFn,
    apiUpload: apiUploadFn,
    apiBlobRequest: apiBlobRequestFn,
  };
}

/*
 * Default unauthenticated client for public portals.
 * Maintains backward compatibility with existing imports.
 */
const defaultClient = createApiClient();

/** Makes an unauthenticated API request with correlation ID propagation. */
export const apiRequest = defaultClient.apiRequest;
